import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { Eye, EyeOff, Link2, Plus, Trash2, UploadCloud } from "lucide-react";
import ConfirmDialog from "../../components/ConfirmDialog";
import MediaRenderer from "../../components/MediaRenderer";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { uploadAdminMedia } from "../../lib/mediaUpload";
import { ADMIN_EMAILS } from "../../lib/platformData";
import { mergeSiteContent } from "../../utils/helpers";

const homeVisibility = [
  ["homeNewsHero", "News Hero", "Bannière vidéo affichée tout en haut"],
  ["homeYms", "Artiste YMS", "Présentation artiste et galerie"],
  ["homeSession", "Présentation Unlimited", "Texte, logo et accès studio"],
  ["homeCovers", "Pochettes", "Slider des pochettes et liens musique"],
  ["homeMerch", "Latdior", "Aperçu de la collection"],
  ["homeVideos", "Vidéos", "Dernières vidéos YouTube"],
  ["homeCtas", "Blocs contact", "Contact, newsletter et réseaux"],
];

function Field({ label, value, onChange, textarea = false, placeholder = "" }) {
  const Input = textarea ? "textarea" : "input";
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <Input
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== (value || "")) onChange(draft);
        }}
        className={`w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-white outline-none transition focus:border-red-500 ${
          textarea ? "min-h-[120px]" : ""
        }`}
      />
    </label>
  );
}

function getIn(obj, path) {
  return path.split(".").reduce((value, key) => value?.[key], obj);
}

function setIn(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cursor = clone;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = Array.isArray(cursor[key]) ? [...cursor[key]] : { ...(cursor[key] || {}) };
    cursor = cursor[key];
  });
  return clone;
}

function VisibilityToggle({ label, description, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-white/10 bg-black p-4 transition hover:border-white/25">
      <span>
        <span className="flex items-center gap-2 text-sm font-bold uppercase">
          {checked ? <Eye size={16} className="text-emerald-400" /> : <EyeOff size={16} className="text-gray-500" />}
          {label}
        </span>
        <span className="mt-1 block text-xs text-gray-500">{description}</span>
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}

function MediaCollectionEditor({
  title,
  description,
  items,
  links,
  itemLabels,
  uploading,
  onAdd,
  onReplace,
  onDelete,
  onUrlChange,
  onLinkChange,
  onLabelChange,
}) {
  return (
    <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Médias accueil</p>
          <h2 className="mt-2 text-2xl font-black uppercase">{title}</h2>
          <p className="mt-2 max-w-2xl text-sm text-gray-400">{description}</p>
        </div>
        <label className={`inline-flex w-fit cursor-pointer items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black transition hover:bg-red-600 hover:text-white ${uploading ? "pointer-events-none opacity-60" : ""}`}>
          <Plus size={15} /> {uploading ? "Import..." : "Ajouter"}
          <input
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) onAdd(file);
            }}
          />
        </label>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/15 bg-black p-8 text-center text-sm text-gray-500">
          Aucun média pour le moment.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((src, index) => (
            <article key={`${src}-${index}`} className="rounded-lg border border-white/10 bg-black p-3">
              <MediaRenderer
                src={src}
                type="auto"
                alt={`${title} ${index + 1}`}
                background
                controls
                className="aspect-video w-full rounded object-cover"
              />
              <div className="mt-3 space-y-3">
                <Field label="URL du média" value={src} onChange={(value) => onUrlChange(index, value)} />
                {links && (
                  <div className="relative">
                    <Link2 className="pointer-events-none absolute left-3 top-[31px] text-gray-500" size={15} />
                    <div className="[&_input]:pl-9">
                      <Field
                        label="Lien au clic"
                        value={links[index] || ""}
                        placeholder="YouTube, Spotify ou page interne"
                        onChange={(value) => onLinkChange(index, value)}
                      />
                    </div>
                  </div>
                )}
                {itemLabels && (
                  <Field
                    label="Texte du bouton"
                    value={itemLabels[index] || ""}
                    placeholder="Voir / commander"
                    onChange={(value) => onLabelChange(index, value)}
                  />
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/15 px-3 py-2 text-xs font-bold uppercase transition hover:border-white ${uploading ? "pointer-events-none opacity-60" : ""}`}>
                  <UploadCloud size={14} /> Remplacer
                  <input
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = "";
                      if (file) onReplace(index, file);
                    }}
                  />
                </label>
                <button
                  onClick={() => onDelete(index)}
                  className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-2 text-xs font-bold uppercase transition hover:bg-red-700"
                >
                  <Trash2 size={14} /> Supprimer
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AdminAccueil() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const toast = useToast();
  const [content, setContent] = useState(() => mergeSiteContent());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  const isAdmin = Boolean(user && ADMIN_EMAILS.includes(user.email));

  useEffect(() => {
    if (!isAdmin) return undefined;
    const off = onSnapshot(doc(db, "admin", "content"), (snap) => {
      setContent(mergeSiteContent(snap.exists() ? snap.data() : {}));
    });
    return () => off();
  }, [isAdmin]);

  const savePath = async (path, value, successTitle = "Accueil sauvegardé") => {
    const next = setIn(content, path, value);
    setContent(next);
    setSaving(true);
    try {
      await setDoc(doc(db, "admin", "content"), sanitizeForFirestore(next), { merge: true });
      toast.success(successTitle);
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadPath = async (file, folder, path, label) => {
    if (!file) return;
    setUploading(label);
    try {
      const url = await uploadAdminMedia(file, folder);
      await savePath(path, url, "Média mis à jour");
    } catch (err) {
      console.error("Upload accueil impossible", err);
      toast.error("Upload impossible", err.message);
    } finally {
      setUploading("");
    }
  };

  const saveArray = (path, items, successTitle = "Accueil sauvegardé") => {
    return savePath(path, items, successTitle);
  };

  const uploadArrayItem = async (file, folder, path, label, index = null) => {
    if (!file) return;
    setUploading(label);
    try {
      const url = await uploadAdminMedia(file, folder);
      const current = [...(getIn(content, path) || [])];
      if (index === null) current.push(url);
      else current[index] = url;
      await saveArray(path, current, "Média mis à jour");
    } catch (err) {
      console.error("Upload accueil impossible", err);
      toast.error("Upload impossible", err.message);
    } finally {
      setUploading("");
    }
  };

  const updateArrayItem = (path, index, value, successTitle = "Accueil sauvegardé") => {
    const current = [...(getIn(content, path) || [])];
    current[index] = value;
    return saveArray(path, current, successTitle);
  };

  const requestArrayDelete = (path, index, linkedPaths = []) => {
    setPendingDelete({
      path,
      index,
      linkedPaths: Array.isArray(linkedPaths) ? linkedPaths : linkedPaths ? [linkedPaths] : [],
    });
  };

  const confirmArrayDelete = async () => {
    if (!pendingDelete) return;
    const next = [...(getIn(content, pendingDelete.path) || [])];
    next.splice(pendingDelete.index, 1);
    let updated = setIn(content, pendingDelete.path, next);
    (pendingDelete.linkedPaths || []).forEach((linkedPath) => {
      const linked = [...(getIn(updated, linkedPath) || [])];
      linked.splice(pendingDelete.index, 1);
      updated = setIn(updated, linkedPath, linked);
    });
    setPendingDelete(null);
    setContent(updated);
    setSaving(true);
    try {
      await setDoc(doc(db, "admin", "content"), sanitizeForFirestore(updated), { merge: true });
      toast.success("Média supprimé");
    } catch (err) {
      toast.error("Suppression impossible", err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <main className="min-h-screen bg-black p-8 text-white">Chargement...</main>;

  if (!user || !isAdmin) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Admin Accueil</h1>
          <p className="mt-3 text-gray-400">Connexion admin requise.</p>
          {user && !isAdmin ? (
            <button onClick={logout} className="mt-6 rounded-full bg-red-600 px-5 py-3 text-sm font-bold uppercase">
              Changer de compte
            </button>
          ) : (
            <button onClick={loginWithGoogle} className="mt-6 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">
              Connexion Google
            </button>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black p-4 text-white md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">Admin</p>
            <h1 className="mt-2 text-4xl font-black uppercase">Accueil</h1>
            <p className="mt-2 text-sm text-gray-400">
              Modifie uniquement les contenus de la page d'accueil.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admin/news" className="rounded-full border border-red-500/50 px-4 py-2 text-sm font-bold uppercase text-red-100">
              Gérer les news
            </Link>
            <Link to="/admin" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              Dashboard
            </Link>
            <Link to="/" className="rounded-full bg-white px-4 py-2 text-sm font-bold uppercase text-black">
              Voir le site
            </Link>
          </div>
        </header>

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
          <div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">
                Contrôle d'affichage
              </p>
              <h2 className="mt-2 text-2xl font-black uppercase">Sections de l'accueil</h2>
              <p className="mt-2 max-w-2xl text-sm text-gray-400">
                Active ou masque chaque bloc sans supprimer son contenu.
              </p>
            </div>
            <span className="text-xs text-gray-500">{saving ? "Sauvegarde..." : "Auto-save"}</span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {homeVisibility.map(([key, label, description]) => (
              <VisibilityToggle
                key={key}
                label={label}
                description={description}
                checked={content.visibility[key] !== false}
                onChange={(value) => savePath(`visibility.${key}`, value, value ? "Section affichée" : "Section masquée")}
              />
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
          <div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">
                Première section
              </p>
              <h2 className="mt-2 text-2xl font-black uppercase">News Hero</h2>
              <p className="mt-2 max-w-2xl text-sm text-gray-400">
                La bannière vidéo affichée tout en haut de l'accueil.
              </p>
            </div>
            <label className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase">
              <input
                type="checkbox"
                checked={content.visibility.homeNewsHero !== false}
                onChange={(event) => savePath("visibility.homeNewsHero", event.target.checked)}
              />
              Afficher
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Field label="Titre" value={content.pages.home.newsHero.title} onChange={(v) => savePath("pages.home.newsHero.title", v)} />
            </div>
            <div className="md:col-span-2">
              <Field textarea label="Sous-titre" value={content.pages.home.newsHero.subtitle} onChange={(v) => savePath("pages.home.newsHero.subtitle", v)} />
            </div>
            <Field label="URL vidéo de fond" value={content.pages.home.newsHero.video} onChange={(v) => savePath("pages.home.newsHero.video", v)} />
            <Field label="URL image fallback" value={content.pages.home.newsHero.fallbackImage} onChange={(v) => savePath("pages.home.newsHero.fallbackImage", v)} />
            <Field label="Lien YouTube Regarder" value={content.pages.home.newsHero.watchUrl} onChange={(v) => savePath("pages.home.newsHero.watchUrl", v)} />
            <Field label="Texte bouton Regarder" value={content.pages.home.newsHero.watchLabel} onChange={(v) => savePath("pages.home.newsHero.watchLabel", v)} />
            <Field label="Lien Découvrir" value={content.pages.home.newsHero.discoverUrl} onChange={(v) => savePath("pages.home.newsHero.discoverUrl", v)} />
            <Field label="Texte bouton Découvrir" value={content.pages.home.newsHero.discoverLabel} onChange={(v) => savePath("pages.home.newsHero.discoverLabel", v)} />
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/admin/news"
              className="inline-flex items-center rounded-full border border-red-500/50 px-5 py-2 text-xs font-bold uppercase text-red-100 transition hover:bg-red-600 hover:text-white"
            >
              Gérer toutes les news
            </Link>
            <label className={`cursor-pointer rounded-full bg-white px-5 py-2 text-xs font-bold uppercase text-black ${uploading ? "pointer-events-none opacity-60" : ""}`}>
              {uploading === "heroVideo" ? "Upload vidéo..." : "Remplacer la vidéo"}
              <input
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  uploadPath(file, "site/home/news", "pages.home.newsHero.video", "heroVideo");
                }}
              />
            </label>
            <label className={`cursor-pointer rounded-full border border-white/15 px-5 py-2 text-xs font-bold uppercase ${uploading ? "pointer-events-none opacity-60" : ""}`}>
              {uploading === "heroFallback" ? "Upload image..." : "Remplacer l'image fallback"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  uploadPath(file, "site/home/news", "pages.home.newsHero.fallbackImage", "heroFallback");
                }}
              />
            </label>
          </div>
        </section>

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">
                Section YMS
              </p>
              <h2 className="mt-2 text-2xl font-black uppercase">Hero artiste</h2>
            </div>
            <span className="text-xs text-gray-500">{saving ? "Sauvegarde..." : "Auto-save"}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Badge" value={content.pages.home.ymsBadge} onChange={(v) => savePath("pages.home.ymsBadge", v)} />
            <Field label="Lien bouton" value={content.pages.home.ymsButtonUrl} onChange={(v) => savePath("pages.home.ymsButtonUrl", v)} />
            <Field label="Titre" value={content.texts.ymsTitle} onChange={(v) => savePath("texts.ymsTitle", v)} />
            <Field label="Bouton" value={content.texts.ymsButton} onChange={(v) => savePath("texts.ymsButton", v)} />
            <div className="md:col-span-2">
              <Field textarea label="Description" value={content.texts.ymsDescription} onChange={(v) => savePath("texts.ymsDescription", v)} />
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">
            Section Unlimited
          </p>
          <h2 className="mt-2 text-2xl font-black uppercase">Présentation</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Titre" value={content.texts.sessionTitle} onChange={(v) => savePath("texts.sessionTitle", v)} />
            <Field label="Logo URL" value={content.pages.home.sessionLogo} onChange={(v) => savePath("pages.home.sessionLogo", v)} />
            <Field label="Bouton 1" value={content.texts.sessionButton1} onChange={(v) => savePath("texts.sessionButton1", v)} />
            <Field label="Lien bouton 1" value={content.pages.home.sessionButton1Url} onChange={(v) => savePath("pages.home.sessionButton1Url", v)} />
            <Field label="Bouton 2" value={content.texts.sessionButton2} onChange={(v) => savePath("texts.sessionButton2", v)} />
            <Field label="Lien bouton 2" value={content.pages.home.sessionButton2Url} onChange={(v) => savePath("pages.home.sessionButton2Url", v)} />
            <div className="md:col-span-2">
              <Field textarea label="Description" value={content.texts.sessionDescription} onChange={(v) => savePath("texts.sessionDescription", v)} />
            </div>
          </div>
          <div className="mt-5">
            <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase ${uploading ? "pointer-events-none opacity-60" : ""}`}>
              <UploadCloud size={14} /> {uploading === "sessionLogo" ? "Upload logo..." : "Remplacer le logo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  uploadPath(file, "site/home", "pages.home.sessionLogo", "sessionLogo");
                }}
              />
            </label>
          </div>
        </section>

        <MediaCollectionEditor
          title="Latdior sur l'accueil"
          description="Ces médias alimentent uniquement la section Latdior affichée sur l'accueil. Tu peux gérer l'image, le lien au clic et le texte du bouton de chaque carte."
          items={content.merch || []}
          links={content.merchLinks || []}
          itemLabels={content.merchLabels || []}
          uploading={uploading === "merch"}
          onAdd={(file) => uploadArrayItem(file, "site/merch", "merch", "merch")}
          onReplace={(index, file) => uploadArrayItem(file, "site/merch", "merch", "merch", index)}
          onDelete={(index) => requestArrayDelete("merch", index, ["merchLinks", "merchLabels"])}
          onUrlChange={(index, value) => updateArrayItem("merch", index, value, "Média Latdior accueil mis à jour")}
          onLinkChange={(index, value) => updateArrayItem("merchLinks", index, value, "Lien Latdior accueil mis à jour")}
          onLabelChange={(index, value) => updateArrayItem("merchLabels", index, value, "Texte Latdior accueil mis à jour")}
        />

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Latdior accueil</p>
          <h2 className="mt-2 text-2xl font-black uppercase">Textes et boutons</h2>
          <p className="mt-2 max-w-2xl text-sm text-gray-400">
            Ces réglages ne modifient que le bloc Latdior visible sur l'accueil.
          </p>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Eyebrow" value={content.pages.home.latdiorPreview?.eyebrow} onChange={(v) => savePath("pages.home.latdiorPreview.eyebrow", v)} />
            <Field label="Titre" value={content.pages.home.latdiorPreview?.title} onChange={(v) => savePath("pages.home.latdiorPreview.title", v)} />
            <div className="md:col-span-2">
              <Field textarea label="Description" value={content.pages.home.latdiorPreview?.description} onChange={(v) => savePath("pages.home.latdiorPreview.description", v)} />
            </div>
            <Field label="Bouton principal" value={content.pages.home.latdiorPreview?.buttonLabel} onChange={(v) => savePath("pages.home.latdiorPreview.buttonLabel", v)} />
            <Field label="Lien bouton principal" value={content.pages.home.latdiorPreview?.buttonUrl} onChange={(v) => savePath("pages.home.latdiorPreview.buttonUrl", v)} />
            <Field label="Bouton secondaire" value={content.pages.home.latdiorPreview?.secondaryLabel} onChange={(v) => savePath("pages.home.latdiorPreview.secondaryLabel", v)} />
            <Field label="Lien bouton secondaire" value={content.pages.home.latdiorPreview?.secondaryUrl} onChange={(v) => savePath("pages.home.latdiorPreview.secondaryUrl", v)} />
            <Field label="Libellé par défaut des cartes" value={content.pages.home.latdiorPreview?.cardLabel} onChange={(v) => savePath("pages.home.latdiorPreview.cardLabel", v)} />
          </div>
        </section>

        <MediaCollectionEditor
          title="Pochettes"
          description="Ajoute, remplace ou supprime les pochettes du slider. Colle un lien Spotify open.spotify.com pour ouvrir le player directement sur le site, ou un lien YouTube/page interne si besoin."
          items={content.covers || []}
          links={content.coverLinks || []}
          uploading={uploading === "covers"}
          onAdd={(file) => uploadArrayItem(file, "site/covers", "covers", "covers")}
          onReplace={(index, file) => uploadArrayItem(file, "site/covers", "covers", "covers", index)}
          onDelete={(index) => requestArrayDelete("covers", index, "coverLinks")}
          onUrlChange={(index, value) => updateArrayItem("covers", index, value, "Pochette mise à jour")}
          onLinkChange={(index, value) => updateArrayItem("coverLinks", index, value, "Lien pochette mis à jour")}
        />

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Pochettes</p>
          <h2 className="mt-2 text-2xl font-black uppercase">Titre du slider</h2>
          <div className="mt-5">
            <Field
              label="Titre affiché au-dessus des pochettes"
              value={content.pages.home.coversTitle}
              onChange={(value) => savePath("pages.home.coversTitle", value)}
            />
          </div>
        </section>

        <MediaCollectionEditor
          title="Images YMS"
          description="Ces médias alimentent la galerie artiste visible dans l'accueil. Tu peux utiliser une image ou une vidéo."
          items={content.ymsImages || []}
          uploading={uploading === "ymsImages"}
          onAdd={(file) => uploadArrayItem(file, "site/yms", "ymsImages", "ymsImages")}
          onReplace={(index, file) => uploadArrayItem(file, "site/yms", "ymsImages", "ymsImages", index)}
          onDelete={(index) => requestArrayDelete("ymsImages", index)}
          onUrlChange={(index, value) => updateArrayItem("ymsImages", index, value, "Média YMS mis à jour")}
        />

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Bas de page</p>
          <h2 className="mt-2 text-2xl font-black uppercase">Cartes contact et réseaux</h2>
          <p className="mt-2 text-sm text-gray-400">
            Modifie les textes et les liens des cartes affichées tout en bas de l'accueil.
          </p>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Titre contact" value={content.pages.home.ctas.contactTitle} onChange={(v) => savePath("pages.home.ctas.contactTitle", v)} />
            <Field label="Texte bouton contact" value={content.pages.home.ctas.contactLabel} onChange={(v) => savePath("pages.home.ctas.contactLabel", v)} />
            <Field label="Lien contact" value={content.pages.home.ctas.contactUrl} onChange={(v) => savePath("pages.home.ctas.contactUrl", v)} />
            <Field label="Description contact" textarea value={content.pages.home.ctas.contactDescription} onChange={(v) => savePath("pages.home.ctas.contactDescription", v)} />
            <Field label="Titre newsletter" value={content.pages.home.ctas.newsletterTitle} onChange={(v) => savePath("pages.home.ctas.newsletterTitle", v)} />
            <Field label="Description newsletter" textarea value={content.pages.home.ctas.newsletterDescription} onChange={(v) => savePath("pages.home.ctas.newsletterDescription", v)} />
            <Field label="Titre réseaux" value={content.pages.home.ctas.socialTitle} onChange={(v) => savePath("pages.home.ctas.socialTitle", v)} />
            <Field label="Description réseaux" textarea value={content.pages.home.ctas.socialDescription} onChange={(v) => savePath("pages.home.ctas.socialDescription", v)} />
            <Field label="Facebook" value={content.pages.home.ctas.facebookUrl} onChange={(v) => savePath("pages.home.ctas.facebookUrl", v)} />
            <Field label="Instagram" value={content.pages.home.ctas.instagramUrl} onChange={(v) => savePath("pages.home.ctas.instagramUrl", v)} />
            <Field label="YouTube" value={content.pages.home.ctas.youtubeUrl} onChange={(v) => savePath("pages.home.ctas.youtubeUrl", v)} />
            <Field label="Twitter / X" value={content.pages.home.ctas.twitterUrl} onChange={(v) => savePath("pages.home.ctas.twitterUrl", v)} />
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Supprimer ce média ?"
        message="Le média sera retiré de l'accueil. Cette action ne modifie pas les autres contenus du site."
        confirmLabel="Supprimer"
        onConfirm={confirmArrayDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </main>
  );
}
